import React, { useEffect, useState } from "react";
import { getCookies } from "cookies-next";
import axios from "axios";
import Swal from "sweetalert2";
import BreadCrump from '../tools/breadcrump';
import HeaderOfPage from '../tools/headerOfPage';
import Icons from '../../hoc/icons';
import { useTranslation } from 'react-i18next';
import { config } from "../../data/config";

interface LanguageCode {
    code: string;
    label: string;
}

export default function SocialPage() {
    const Icon = new Icons();
    const { t } = useTranslation();
    const cookie = getCookies();

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeLang, setActiveLang] = useState<string>('en');

    const languages: LanguageCode[] = [
        { code: 'ku', label: 'Kurdish' },
        { code: 'fa', label: 'فارسی' },
        { code: 'en', label: 'English' },
        { code: 'ar', label: 'العربية' },
        { code: 'tr', label: 'Türkçe' }
    ];

    const inputStyle = "py-2 px-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm";

    const breadcrump = [
        { name: 'Dashboard', url: '/dashboard' },
        { name: 'Setting', url: '/dashboard/setting' },
        { name: 'Social Network', url: null },
    ];

    const getMultilingualValue = (field: any, lang: string): string => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field === 'object') {
            return field[lang] || field.en || field.ku || field.fa || field.ar || field.tr || '';
        }
        return '';
    };

    const load = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${config.url}/v1/dashboard/socialnetwork`, {
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const loadedData = res.data.message;
            
            // Normalize multilingual fields - ensure all fields are objects
            if (loadedData) {
                const socialFields = ['facebook', 'whatsapp', 'youtube', 'instagram', 'wechat', 'tiktok', 'telegram', 'telegramchanal', 'snapchat', 'twitter', 'pinterest', 'reddit', 'linkedin', 'discort', 'sorosh', 'rubika', 'igap', 'eitaa'];
                socialFields.forEach(field => {
                    if (typeof loadedData[field] === 'string') {
                        const oldValue = loadedData[field];
                        loadedData[field] = {
                            ku: oldValue || '',
                            fa: oldValue || '',
                            en: oldValue || '',
                            ar: oldValue || '',
                            tr: oldValue || ''
                        };
                    } else if (!loadedData[field] || typeof loadedData[field] !== 'object') {
                        loadedData[field] = { ku: '', fa: '', en: '', ar: '', tr: '' };
                    }
                });
            }
            
            setData(loadedData);
        } catch {
            Swal.fire('Error loading social network data');
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        try {
            await axios.put(`${config.url}/v1/dashboard/socialnetwork/${data.id}`, data, {
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            Swal.fire('Saved');
        } catch {
            Swal.fire('Error saving data');
        }
    };

    useEffect(() => { load(); }, []);

    if (!data) return null;

    const socialGroups = [
        { name: 'پیامرسان', items: [
            { label: 'WhatsApp', icon: <Icon.WhatsApp />, key: 'whatsapp' },
            { label: 'Telegram', icon: <Icon.Telegram />, key: 'telegram' },
            { label: 'Telegram Channel', icon: <Icon.TelegramChanal />, key: 'telegramchanal' },
            { label: 'WeChat', icon: <Icon.Wechat />, key: 'wechat' },
            { label: 'iGap', icon: <img src='/icons/igap.png' width={20} />, key: 'igap' },
            { label: 'Eitaa', icon: <Icon.Eitaa />, key: 'eitaa' },
            { label: 'Sorosh', icon: <Icon.Sapp />, key: 'sorosh' },
            { label: 'Rubika', icon: <Icon.Rubika />, key: 'rubika' },
            { label: 'Discord', icon: <Icon.Discord />, key: 'discort' },
        ]},
        { name: 'شبکه های اجتماعی', items: [
            { label: 'Facebook', icon: <Icon.FaceBook />, key: 'facebook' },
            { label: 'Instagram', icon: <Icon.Instagram />, key: 'instagram' },
            { label: 'X (Twitter)', icon: <Icon.XTwitter />, key: 'twitter' },
            { label: 'Pinterest', icon: <Icon.Pinterest />, key: 'pinterest' },
            { label: 'Reddit', icon: <Icon.Reddit />, key: 'reddit' },
            { label: 'LinkedIn', icon: <Icon.Linkedin />, key: 'linkedin' },
            { label: 'Snapchat', icon: <Icon.Snapchat />, key: 'snapchat' },
        ]},
        { name: 'ویدیو', items: [
            { label: 'YouTube', icon: <Icon.YouTube />, key: 'youtube' },
            { label: 'TikTok', icon: <Icon.Tiktok />, key: 'tiktok' }
        ]}
    ];

    return (
        <div className="bg-gray-100 min-h-[100vh] p-6">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={t("Social Network")} />

            <div className="flex justify-between items-center mb-4">
                <div className="flex gap-2">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => setActiveLang(lang.code)}
                            className={`py-2 px-4 rounded-lg font-medium transition ${
                                activeLang === lang.code
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {lang.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={save}
                    className="py-2 px-5 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
                >
                    {t('Save')}
                </button>
            </div>

            <div className="space-y-6"  dir="ltr">
                {socialGroups.map((group) => (
                    <div key={group.name} className="bg-white shadow-md rounded-xl p-6 space-y-4">
                        <h2 className="text-lg font-bold text-gray-800">{t(group.name)}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {group.items.map((item) => (
                                <div key={item.key} className="space-y-1">
                                    <label className="flex items-center gap-2 text-gray-700 font-medium">
                                        {item.icon} {t(item.label)}
                                    </label>
                                    <input
                                        type="text"
                                        value={getMultilingualValue(data[item.key], activeLang)}
                                        placeholder="Full URL"
                                        className={inputStyle}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            const currentField = data[item.key] || {};
                                            const updatedField = typeof currentField === 'object' 
                                                ? { ...currentField, [activeLang]: newValue }
                                                : { ku: '', fa: '', en: '', ar: '', tr: '', [activeLang]: newValue };
                                            setData({ ...data, [item.key]: updatedField });
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
